import { caseConverterClient, paramsSerializerV0 } from "@/api/client"
import type { List } from "@/api/lists"
import type { User } from "@/api/users"
import type { EmptyObject } from "@/util/types"

import type { ProductType } from "./shared"

export interface DenormalizedWorkspace {
  id: number
  name: string
  productType: ProductType
  isPublic: boolean
  isDefault: boolean

  description?: string
  color?: string
  containingWorkspaceId?: number | null
  customMetadata?: { [index: string]: string | number | boolean | null }
  availableTemplateIds?: number[] | null
  listsTemplateIds?: number[]
  ancestors?: Array<{
    id: number
    name: string
  }>

  createdAt: string
  creator?: User

  // == Derived ==
  lists: List[]
  listsCount: number
  fileFeedsCount: number
  workflowsCount: number
}

export type Workspace = Omit<DenormalizedWorkspace, "lists"> & {
  listIds: number[]
}

interface GetWorkspacesRequestParams {
  productType: ProductType
  page?: number
  pageSize?: number
  containingWorkspaceId?: number | null
  searchTerm?: string
  sortModel?: {
    colId: string
    sort: string
  }
}

interface GetWorkspacesResponse {
  workspaces: DenormalizedWorkspace[]
  count: number
}

export function getWorkspaces(params: GetWorkspacesRequestParams, signal?: AbortSignal) {
  // paramsSerializerV0 drops nulls, so keep the root-only key alive as `=`.
  // The backend coerces the empty value back to nil and takes the root branch.
  const requestParams =
    params.containingWorkspaceId === null
      ? { ...params, containingWorkspaceId: "" }
      : params

  return caseConverterClient.get<GetWorkspacesResponse>("/api/workspaces", {
    params: requestParams,
    paramsSerializer: paramsSerializerV0,
    signal,
  })
}

/**
 * Fetches the denormalized workspaces array that is normally inlined into
 * viewData under the `workspaces` key. Used to lazy-hydrate the Redux store
 * after first paint when the bootstrap blob is slimmed (SlimViewData). The
 * response shape is byte-identical to the viewData value, so it flows through
 * the same `normalizeWorkspaces` normalization path.
 */
export async function getViewDataWorkspaces() {
  return caseConverterClient.get<DenormalizedWorkspace[]>("/api/workspaces/view-data")
}

// Slightly different naming convention to avoid confusion with the getWorkspaceById function.
export function fetchWorkspaceById(workspaceId: number) {
  return caseConverterClient.get<DenormalizedWorkspace>(`/api/workspaces/${workspaceId}`)
}

type MutateWorkspace = Omit<
  Workspace,
  | "isPublic" // Not supported yet
  | "isDefault" // Immutable
  | "createdAt"
  | "creator"
  | "lists"
  | "listsCount"
  | "fileFeedsCount"
>

type CreateWorkspace = Partial<MutateWorkspace> &
  Pick<MutateWorkspace, "name" | "productType">

export async function postWorkspace(workspace: CreateWorkspace) {
  return caseConverterClient.post<DenormalizedWorkspace>("/api/workspaces", {
    ...workspace,
    // TODO: Allow non-public workspaces
    isPublic: true,
  })
}

export type UpdateWorkspace = Omit<Partial<MutateWorkspace>, "productType">

export async function putWorkspace(workspaceId: number, workspace: UpdateWorkspace) {
  return caseConverterClient.put<DenormalizedWorkspace>(
    `/api/workspaces/${workspaceId}`,
    workspace,
  )
}

export async function moveWorkspace(
  workspaceId: number,
  containingWorkspaceId: number | null,
) {
  return caseConverterClient.patch<DenormalizedWorkspace>(
    `/api/workspaces/${workspaceId}`,
    { containingWorkspaceId },
  )
}

export async function deleteWorkspace(workspaceId: number) {
  return caseConverterClient.delete<Workspace>(`/api/workspaces/${workspaceId}`)
}

export async function getWorkspaceLists(workspaceId: number) {
  return caseConverterClient.get<{ lists: Array<List> }>(
    `/api/workspaces/${workspaceId}/lists`,
  )
}

export async function moveListToWorkspace(
  workspaceId: number,
  listId: number,
  newWorkspaceId: number,
) {
  return caseConverterClient.post<EmptyObject>(
    `/api/workspaces/${workspaceId}/lists/${listId}/move-to-workspace`,
    { newWorkspaceId },
  )
}

export async function migrateWorkspaceWorkflowsToV2(workspaceId: number) {
  return caseConverterClient.post<{ message: string }>(
    `/api/workspaces/${workspaceId}/migrate-workflows-to-v2`,
  )
}
